<template>
  <UIModal v-model="isOpen" @closed="resetForm">
    <template v-if="token" #default>
      <div class="tw:sm:min-w-md">
        <div class="tw:flex tw:flex-col tw:gap-6 tw:p-2">
          <div class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:mx-auto tw:max-w-3xs">
            <img :alt="token.name" :src="token.imageUrl" class="tw:w-full" />
            <div class="tw:text-white tw:text-center">
              <h6 class="tw:font-medium tw:text-2xl">{{ computedTokenName }}</h6>
              <p class="tw:text-sm">{{ $t(token.description || '', { count: 0 }) }}</p>
            </div>
          </div>
          <div
            v-if="!houseInRent"
            class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:shadow-xl tw:p-2 tw:border tw:border-[#1E353E] tw:rounded-xl"
          >
            <UIInput
              v-model="receiverAddress"
              :placeholder="$t('walletAddress')"
              class="tw:bg-[#5C5D5E]/50 tw:px-2 tw:rounded-md tw:w-full tw:h-8"
            />
            <HalloweenGenericButton
              :disabled="!receiverAddress || isProcessing"
              @click="sendNft(receiverAddress, token.nftAddress || '')"
              >{{ $t('walletSend') }}</HalloweenGenericButton
            >
          </div>
          <div
            class="tw:flex tw:justify-between tw:items-center tw:gap-2 tw:shadow-xl tw:p-2 tw:border tw:border-[#1E353E] tw:rounded-xl"
          >
            <div class="tw:flex tw:items-center tw:gap-2">
              <template v-if="isSupported && token?.nftAddress">
                <IconsCopy v-if="!copied" class="tw:w-5 tw:h-5" @click="handleCopyAddress(token.nftAddress)" />
                <IconsCheckMark v-else class="tw:w-5 tw:h-5 tw:text-white" />
              </template>
              <NuxtLink :to="getTokenAddressLink(token.nftAddress || '')" external target="_blank">
                <IconsLinkNewTab class="tw:w-5 tw:h-5" />
              </NuxtLink>
            </div>
            <AddNftButton :token="token" />
          </div>
        </div>
      </div>
    </template>
  </UIModal>
</template>

<script lang="ts" setup>
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { AddNftButton } from '~/features/wallet';
import { ERC721__factory } from '~/public/abi/evm/types';
import { useRentEventStore } from '~/stores/rent-event-store';
import type { TokenDescription } from '~/types/token';
import { Formatters } from '~/utils/formatters/formatters';

const { token } = defineProps<{
  token: TokenDescription | null;
}>();

const isOpen = defineModel<boolean>({ default: false });

const storageItemsEventBus = useEventBus<void>('storage-items');

const rent = useRentEventStore();

const houseInRent = computed(() => {
  const rentedHouses = rent.$state.userHouses.filter((h) => h.rentedOut);
  return rentedHouses.some(
    (h) => h.address.toLowerCase() === token?.nftAddress?.toLowerCase() && String(h.tokenId) === String(token.tokenId)
  );
});

const {
  blockchain: { blockScanUrl }
} = useEnvs();
const { sendContractMethod } = useSendContractMethod();
const { getSigner } = useProvider();
const { address } = useWeb3ModalAccount();
const store = useMainStore();
const { t } = useI18n();

const receiverAddress = ref<string>('');
const amount = ref<string>('0');
const isProcessing = ref(false);

function resetForm() {
  receiverAddress.value = '';
  amount.value = '0';
  isProcessing.value = false;
}

async function sendNft(to: string, nftAddress: string) {
  if (!address.value || !token?.tokenId) return;
  try {
    isProcessing.value = true;
    const transaction = await sendContractMethod({
      contract: ERC721__factory.connect(nftAddress, await getSigner()),
      methodName: 'safeTransferFrom(address,address,uint256)',
      args: [address.value, to, token.tokenId]
    });

    if (!transaction) return false;

    saveHashToLocalStorage(t('walletTransferToken'), transaction.hash);
    store.updateVersion();

    await transaction.wait(2);

    storageItemsEventBus.emit();

    modifyHistoryHashStatus(transaction.hash, 'Done');
    store.updateVersion();
  } catch (error) {
    console.log('error', error);
  } finally {
    isProcessing.value = false;
  }
}

const computedTokenName = computed(() =>
  t(Formatters.text.formatTranslationKey(token?.name || '') || '', { count: 0 })
);

const getTokenAddressLink = (tokenAddress: string) => blockScanUrl + '/token/' + tokenAddress;

const { copy, isSupported, copied } = useClipboard();
const handleCopyAddress = (tokenAddress: string) => copy(tokenAddress);
</script>
